import { useMutation, useQueryClient } from '@tanstack/react-query'
import { set as _set } from 'radash'

import { getServerLikesActive } from '@wwt/core/admin'

import { assert } from '@/shared/lib/assert.ts'

import { SetTypes, setKeys } from '../config/setKeys.ts'
import { createSetQuery } from '../query/createSetQuery.ts'
import { SetMutationUtils } from '../utils/setMutationUtils.ts'

export const useCreateSetMutation = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: setKeys.create(),
		mutationFn: async (title: string) => {
			/**
			 * If local likes enabled we create mock set and return it without requests
			 */
			if (!getServerLikesActive()) {
				return SetMutationUtils.generateSet({ title })
			}

			// TODO: Remove token usage
			return await createSetQuery(title)
		},
		onMutate: (title: string) => {
			const keys = {
				sets: setKeys.all()
			}

			queryClient.cancelQueries({ queryKey: keys.sets })

			const previousSets = queryClient.getQueryData<SetTypes['all']>(keys.sets)

			const optimisticSet = SetMutationUtils.generateSet({ title })

			queryClient.setQueryData<SetTypes['all']>(keys.sets, saved =>
				SetMutationUtils.addSelectedSetToList(saved, optimisticSet)
			)

			return {
				keys,
				previous: { sets: previousSets },
				optimistic: { set: optimisticSet }
			}
		},

		/* prettier-ignore */
		onError: (_err, _title, context) => {
			assert(context !== undefined, 'Context not found in onError on create set')

			queryClient.setQueryData<SetTypes['all']>(context.keys.sets, context.previous.sets)
		},

		onSuccess: (createdSet, _title, context) => {
			assert(
				context !== undefined,
				'Context not found in onSuccess on create set'
			)

			queryClient.setQueryData<SetTypes['all']>(context.keys.sets, saved =>
				SetMutationUtils.replaceTemporalySetWithNew(
					saved,
					context.optimistic.set.id,
					createdSet
				)
			)
		}
	})

	return mutation
}
